import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/system";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useQueryParams } from "hooks/useQueryParams";
import { Suspense, useDeferredValue, useState } from "react";

import { CorrectionClosureBreakdown, CorrectionClosureSummary } from "./CorrectionClosureTile";
import { CorrectionComplianceSummary } from "./CorrectionComplianceTile";
import { MONTH_LABELS } from "./correctionTracking";
import { FlaggedSourceBreakdown, FlaggedSourceSummary } from "./FlaggedSourceTile";
import { removeOmnisearchTerm } from "./omnisearch";
import { useVendorDashboardEnabled } from "./useVendorDashboardEnabled";
import { VendorBreakdownTile } from "./VendorBreakdownTile";
import { VENDOR_DASHBOARD_STORAGE_KEY } from "./VendorDashboardToggle";

// The metrics the dashboard can present. Each swaps the full set of charts +
// vendor table; the dropdown in the tile's upper-right corner selects one.
const DASHBOARD_METRICS = {
  compliance: "30-Day Correction Compliance",
  closure: "Days to Correction Closure",
  flaggedSource: "Encamp vs EPA Flagged",
} as const;
type DashboardMetric = keyof typeof DASHBOARD_METRICS;

// Subtitle over each metric's monthly chart, naming the date the bars group
// by — every monthly axis on this dashboard buckets by when the issue was
// raised (correction requested / discrepancy flagged), not when the manifest
// shipped, and the label should say so.
const MONTHLY_TREND_LABELS: Record<DashboardMetric, string> = {
  compliance: "Corrections by month requested",
  closure: "Closed discrepancies by month raised",
  flaggedSource: "Discrepancies by month flagged",
};

// Explains each metric's purpose behind the info icon next to the title, so a
// viewer knows what the summary + monthly charts are measuring.
const METRIC_INFO: Record<DashboardMetric, string> = {
  compliance:
    "The share of EPA correction requests resolved within 30 days of the request date. Each month's bar splits its requests into resolved on time, resolved late, past due (no response, deadline passed), and awaiting response.",
  closure:
    "How long correction requests take to close, grouped by the month the discrepancy was raised. Lower is better.",
  flaggedSource:
    "How discrepancies were first flagged — by Encamp's own checks versus an EPA correction request — grouped by the month flagged.",
};

const METRIC_STORAGE_KEY = "waste-manifests-vendor-dashboard-metric";

interface Props {
  shippedDateYear: number;
  /**
   * Whether the host's manifest grid can filter on the date a discrepancy was
   * raised. The merged shipping-record list can't — its omnisearch reads
   * shipping paperwork, which has no route to `manifestDiscrepancies` — so it
   * passes false, and the two discrepancy tiles drop their monthly
   * click-to-filter rather than emitting a filter the grid would ignore while
   * dropping the year scope with it. The closure chart keeps its own month
   * selection either way: only the grid filter is unavailable. Defaults to
   * true.
   */
}

export function VendorDashboard({ shippedDateYear }: Props) {
  const theme = useTheme();
  const enabled = useVendorDashboardEnabled();
  const backgroundColor = theme.palette.canvas;

  const [open] = useLocalStorageState<boolean>(VENDOR_DASHBOARD_STORAGE_KEY, {
    defaultValue: true,
  });
  // Which metric the tile presents. Eager value drives the title + dropdown
  // (instant feedback); the deferred value drives the suspense-based content,
  // so the previous metric's charts stay on screen while the newly selected
  // metric's queries resolve instead of blanking to skeletons.
  const [storedMetric, setMetric] = useLocalStorageState<DashboardMetric>(METRIC_STORAGE_KEY, {
    defaultValue: "compliance",
  });
  // localStorage is user-editable; fall back to the default rather than
  // rendering an undefined title if the stored value isn't a known metric.
  const metric: DashboardMetric = storedMetric in DASHBOARD_METRICS ? storedMetric : "compliance";
  const deferredMetric = useDeferredValue(metric);
  // Vendor selection drives the summary, the table (row highlight + toggle),
  // and the monthly chart (overlay). We keep two values:
  //   - selectedVendor: eager — updates instantly, drives the title, the
  //     filter chip, and the table row highlight so the click feels snappy.
  //   - deferredVendor: lags during transition — used for the data-fetching
  //     queries (summary + monthly overlay), which means the old chart stays
  //     visible while the new query resolves rather than blanking to a
  //     suspense fallback.
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const deferredVendor = useDeferredValue(selectedVendor);
  const { setFilters } = useQueryParams<{ omnisearch?: string }>();
  const handleSelectVendor = (vendor: string | null) => {
    // Deselecting (chip delete or table row toggle-off) also removes the
    // vendor filter the selection put on the manifests grid. Centralized here
    // so every deselection path behaves the same; re-removal is idempotent.
    if (vendor === null) {
      setFilters((prev) => ({
        ...prev,
        omnisearch: removeOmnisearchTerm(prev.omnisearch ?? "", "vendor"),
      }));
    }
    setSelectedVendor(vendor);
  };
  // Month selection (closure metric only): clicking a point on the monthly
  // trend narrows the summary card and vendor table to that shipped month.
  // Same eager/deferred split as the vendor — the chip and chart marker
  // respond instantly, the month-filtered vendor query lags behind so the
  // existing table stays visible while it loads. (The summary narrows
  // client-side from already-fetched month data, so it uses the eager value.)
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const deferredMonth = useDeferredValue(selectedMonth);
  const handleSelectMonth = (month: number | null) => {
    setSelectedMonth(month);
  };

  if (!enabled) {
    return null;
  }

  return (
    <Collapse in={open}>
      {/* 16px page gutters to match DashboardLayout (waste overview et al).
          No bottom padding — the grid section below owns the single 16px
          gutter between the two tiles. */}
      <Box
        sx={{
          background: backgroundColor,
          paddingX: 2,
          paddingTop: 2,
          paddingBottom: 0,
        }}
      >
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Vendor stats
        </Typography>
        <Grid container spacing={2}>
          <DashboardTile size={12}>
            {/* Custom header — DashboardTile.Header pushes children to the
                far-right via space-between, but we want the active-vendor
                chip to sit inline next to the title text (with the metric
                dropdown alone on the far right). */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
                  <Typography variant="h5" sx={{ whiteSpace: "nowrap" }}>
                    {DASHBOARD_METRICS[metric]}
                  </Typography>
                  <InfoIconTooltip tooltip={METRIC_INFO[metric]} fontSize="1.1rem" />
                </Stack>
                {selectedVendor && (
                  <Chip
                    label={selectedVendor}
                    color="primary"
                    variant="outlined"
                    onDelete={() => handleSelectVendor(null)}
                    size="small"
                  />
                )}
                {metric === "closure" && selectedMonth && (
                  <Chip
                    label={`${MONTH_LABELS[selectedMonth - 1]} ${shippedDateYear}`}
                    color="primary"
                    variant="outlined"
                    onDelete={() => handleSelectMonth(null)}
                    size="small"
                  />
                )}
              </Stack>
              <Select<DashboardMetric>
                size="small"
                value={metric}
                onChange={(event) => {
                  setMetric(event.target.value);
                  // Month narrowing only exists on the closure metric; clear
                  // it so a hidden filter can't survive a metric round-trip.
                  setSelectedMonth(null);
                }}
                inputProps={{ "aria-label": "Dashboard metric" }}
              >
                {Object.entries(DASHBOARD_METRICS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <DashboardTile.ContentArea>
              <Stack spacing={3}>
                {/* Row 1: summary + monthly chart side-by-side. Same
                    responsive recipe as the Insights facility-mix tile,
                    but split 40/60 (the summary needs less width than
                    twelve months of bars/points): columns stack below lg,
                    with overflow hidden on the summary column so the legend
                    truncates (instead of pushing the chart around) as the
                    row tightens. */}
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={3}
                  alignItems="stretch"
                  divider={
                    <Box
                      sx={{
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        borderTop: {
                          xs: `1px solid ${theme.palette.divider}`,
                          lg: "none",
                        },
                      }}
                    />
                  }
                >
                  {/* overflow hidden on BOTH axes: overflowX alone computes
                      overflow-y to `auto`, which manifests as a phantom
                      vertical scrollbar from the svg's inline baseline gap. */}
                  <Stack spacing={1} sx={{ flex: 2, minWidth: 0, overflow: "hidden" }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {`${selectedVendor ?? "Overall"} · ${
                        metric === "closure" && selectedMonth
                          ? `${MONTH_LABELS[selectedMonth - 1]} ${shippedDateYear}`
                          : shippedDateYear
                      }`}
                    </Typography>
                    <Suspense fallback={<SummarySkeleton metric={deferredMetric} />}>
                      {deferredMetric === "compliance" && (
                        <CorrectionComplianceSummary
                          shippedYear={shippedDateYear}
                          skip={!open}
                          selectedVendor={deferredVendor}
                        />
                      )}
                      {deferredMetric === "closure" && (
                        <CorrectionClosureSummary
                          shippedYear={shippedDateYear}
                          skip={!open}
                          selectedVendor={deferredVendor}
                          // Eager, not deferred: the card narrows client-side
                          // from month data it already holds — no fetch to
                          // hide behind a deferred value.
                          selectedMonth={selectedMonth}
                        />
                      )}
                      {deferredMetric === "flaggedSource" && (
                        <FlaggedSourceSummary
                          shippedYear={shippedDateYear}
                          skip={!open}
                          selectedVendor={deferredVendor}
                        />
                      )}
                    </Suspense>
                  </Stack>
                  <Box sx={{ flex: 3, minWidth: 0 }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {MONTHLY_TREND_LABELS[deferredMetric]}
                      </Typography>
                      <Suspense fallback={<ChartSkeleton />}>
                        {deferredMetric === "compliance" && (
                          <VendorBreakdownTile
                            shippedYear={shippedDateYear}
                            view="byMonth"
                            skip={!open}
                            selectedVendor={deferredVendor}
                            onSelectVendor={handleSelectVendor}
                          />
                        )}
                        {deferredMetric === "closure" && (
                          <CorrectionClosureBreakdown
                            shippedYear={shippedDateYear}
                            view="byMonth"
                            skip={!open}
                            selectedVendor={deferredVendor}
                            onSelectVendor={handleSelectVendor}
                            // Eager: drives the chart's selection marker only
                            // (no query in this view depends on the month).
                            selectedMonth={selectedMonth}
                            onSelectMonth={handleSelectMonth}
                          />
                        )}
                        {deferredMetric === "flaggedSource" && (
                          <FlaggedSourceBreakdown
                            shippedYear={shippedDateYear}
                            view="byMonth"
                            skip={!open}
                            selectedVendor={deferredVendor}
                            onSelectVendor={handleSelectVendor}
                          />
                        )}
                      </Suspense>
                    </Stack>
                  </Box>
                </Stack>
                {/* Row 2: vendor table, full width. Uses the eager
                    selectedVendor so the row highlight responds instantly
                    on click; the chart/summary use the deferred value. */}
                <Suspense fallback={<TableSkeleton />}>
                  {deferredMetric === "compliance" && (
                    <VendorBreakdownTile
                      shippedYear={shippedDateYear}
                      view="byVendor"
                      skip={!open}
                      selectedVendor={selectedVendor}
                      onSelectVendor={handleSelectVendor}
                    />
                  )}
                  {deferredMetric === "closure" && (
                    <CorrectionClosureBreakdown
                      shippedYear={shippedDateYear}
                      view="byVendor"
                      skip={!open}
                      selectedVendor={selectedVendor}
                      onSelectVendor={handleSelectVendor}
                      // Deferred: this view refetches per month, so the
                      // existing table stays up while the new query resolves.
                      selectedMonth={deferredMonth}
                      onSelectMonth={handleSelectMonth}
                    />
                  )}
                  {deferredMetric === "flaggedSource" && (
                    <FlaggedSourceBreakdown
                      shippedYear={shippedDateYear}
                      view="byVendor"
                      skip={!open}
                      selectedVendor={selectedVendor}
                      onSelectVendor={handleSelectVendor}
                    />
                  )}
                </Suspense>
              </Stack>
            </DashboardTile.ContentArea>
          </DashboardTile>
        </Grid>
      </Box>
    </Collapse>
  );
}

function SummarySkeleton({ metric }: { metric: DashboardMetric }) {
  if (metric === "flaggedSource") {
    // Mirrors the flagged-source summary's geometry (legend, grouped bars,
    // two caption rows — vertically centered).
    return (
      <Stack spacing={1.5} justifyContent="center" sx={{ flexGrow: 1, minHeight: "18rem" }}>
        <Skeleton variant="text" width={220} height={20} />
        <Skeleton variant="rectangular" height={192} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" height={20} />
      </Stack>
    );
  }
  if (metric === "closure") {
    // Mirrors the closure KPI card's geometry (headline, delta chip,
    // divider, anchor rows — vertically centered) so the layout doesn't
    // shift when data resolves.
    return (
      <Stack spacing={1.5} justifyContent="center" sx={{ flexGrow: 1, minHeight: "18rem" }}>
        <Skeleton variant="text" width={200} height={72} />
        <Skeleton variant="rounded" width={160} height={24} />
        <Skeleton variant="text" height={20} />
        <Skeleton variant="text" width={240} height={20} />
        <Skeleton variant="text" width={240} height={20} />
      </Stack>
    );
  }
  // Mirrors the donut's geometry (240px circle in an 18rem container,
  // vertically centered) so the layout doesn't shift when data resolves.
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ flexGrow: 1, minHeight: "18rem" }}>
      <Skeleton variant="circular" width={240} height={240} />
    </Stack>
  );
}

function ChartSkeleton() {
  return <Skeleton variant="rectangular" height={320} />;
}

function TableSkeleton() {
  return (
    <Stack spacing={1}>
      <Skeleton variant="rectangular" height={32} />
      <Skeleton variant="rectangular" height={32} />
      <Skeleton variant="rectangular" height={32} />
      <Skeleton variant="rectangular" height={32} />
    </Stack>
  );
}
